import clsx from "clsx";
import partition from "lodash-es/partition";
import { type FC } from "react";
import { useBoolean } from "usehooks-ts";

import { AddTermModal } from "./AddTermModal";

import { TERM_ID_QUERY_PARAM } from "components/constants";
import {
  type SectionViewOption,
  SectionViewOptionMenu,
} from "components/menus/SectionViewOptionMenu";
import { TermType } from "components/server-types";
import { t } from "i18n/i18n";
import { Dialog } from "mds/components/Dialog";
import { DragAndDrop } from "mds/components/DragAndDrop";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { AddPlusIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectIsEditingAsOrgAdmin } from "store/selectors";
import { useDnDItemOrdering, useLocalCopy } from "utils/collections";
import { useQueryParam, useQueryParamsActions } from "utils/routing";
import { withQueryParams } from "utils/urls";

const TERMS_EVENT_PREFIX = "Home - Terms / ";

type TermsNavListProps = {
  terms: TermType[];
  onOptionChange: (option: SectionViewOption) => void;
};

const onDropItem = (droppedItem: TermType) => {
  storeApi.terms.partial_update({ id: droppedItem.id, order: droppedItem.order });
};

export const TermsNavList: FC<TermsNavListProps> = ({ terms, onOptionChange }) => {
  const [localTerms, setLocalTerms] = useLocalCopy(terms);
  const { setQueryParams } = useQueryParamsActions();
  const [archivedTerms, activeTerms] = partition(terms, (term) => term.archived_at);
  const currentTermId = useQueryParam(TERM_ID_QUERY_PARAM);
  const canEdit = useAppSelector(selectIsEditingAsOrgAdmin);
  const isSmallScreen = !useIsMdOrLarger();

  const { value: dialogIsOpen, setTrue: openDialog, setFalse: closeDialog } = useBoolean(false);

  const termCollections = [
    { label: t("home.terms.active_terms"), terms: activeTerms },
    { label: t("home.terms.archived"), terms: archivedTerms },
  ];

  const moveItemsLocally = useDnDItemOrdering(localTerms, setLocalTerms);

  return (
    <div
      className={clsx(
        "grid max-h-[calc(100%-24px)] w-[240px] grid-rows-[auto_1fr_auto] rounded-2xl border border-black-tint-90 p-4",
        isSmallScreen && "w-full",
      )}
      role="list"
    >
      <div className="mb-4">
        <SectionViewOptionMenu
          eventPrefix={TERMS_EVENT_PREFIX}
          selectedOption="term"
          onOptionChange={onOptionChange}
        />
      </div>

      <div className="min-h-0 overflow-y-auto">
        {termCollections.map(
          ({ label, terms: collectionTerms }) =>
            collectionTerms.length > 0 && (
              <div className="mb-4" key={label}>
                <h2 className="body-xs p-2 font-medium text-black-tint-40">{label}</h2>

                {collectionTerms.map((term, index) => {
                  // NOTE: DragAndDrop functionality is disabled for Terms at the moment,
                  // but we want to restore it at some point in the future. See
                  // https://app.asana.com/1/914015496185/project/1206079475640061/task/1210744969386349.
                  return (
                    <DragAndDrop
                      // draggable={canEdit}
                      dragItemKind="term"
                      dropContainerId="term"
                      dropIndex={index}
                      // droppable={canEdit}
                      item={term}
                      key={term.id}
                      onDraggedItemHover={moveItemsLocally}
                      onDropItem={onDropItem}
                    >
                      <ListItem
                        className="my-1 min-w-12 justify-between"
                        eventName={`${TERMS_EVENT_PREFIX}term selected`}
                        eventProperties={{ term: term.title }}
                        isSelected={currentTermId === term.id}
                        label={term.title}
                        to={withQueryParams("", { [TERM_ID_QUERY_PARAM]: term.id })}
                        onClick={() => {
                          setQueryParams({ [TERM_ID_QUERY_PARAM]: term.id });
                        }}
                      >
                        <div className="flex w-full min-w-12 items-center justify-between">
                          <h3 className="body-s my-1 w-full min-w-12 cursor-pointer truncate font-medium">
                            {term.title}
                          </h3>
                        </div>
                      </ListItem>
                    </DragAndDrop>
                  );
                })}
              </div>
            ),
        )}
      </div>

      {canEdit && (
        <>
          <SquareButton
            className={clsx("w-full", localTerms.length && "mt-2")}
            eventName={`${TERMS_EVENT_PREFIX}${t("common.add.term")}`}
            kind="tertiary"
            size="s"
            title={t("common.add.term")}
            onClick={openDialog}
          >
            <IconText iconStart={<AddPlusIcon />} text={t("common.add.term")} />
          </SquareButton>
          <Dialog open={dialogIsOpen} title={t("home.terms.add_term_title")} onClose={closeDialog}>
            <AddTermModal
              eventName={`${TERMS_EVENT_PREFIX} Add Term`}
              localTerms={localTerms}
              onClose={closeDialog}
            />
          </Dialog>
        </>
      )}
    </div>
  );
};
